import { GoalVisibilityFilter } from '@/common/constants/filter';
import { authService } from '@/features/auth/services/authService';
import {
    Goal,
    GoalWithDetails,
    GoalWithInfos,
    GoalWithProject,
    GoalWithTodos,
    ProjectBase,
    TeamBase,
    TodoWithPlanners,
    User,
} from '../../../common/types/databaseTypes';
import { Database, supabase } from '@tickitacka/shared';
import { workspaceService } from '@/features/workspace/services/workspaceService';

export type GoalInsert = Database['public']['Tables']['goal']['Insert'];
export type CreateGoalParams = Omit<
    GoalInsert,
    'id' | 'register_date' | 'update_date' | 'enable'
>;

export const goalService = {
    async getHiddenGoalsByUserId(userId: string) {
        const { data: hiddenGoalsData } = await supabase
            .from('user_goal_visibility')
            .select('goal_id')
            .eq('user_id', userId)
            .eq('is_hidden', true);

        return hiddenGoalsData?.map(item => item.goal_id) || [];
    },

    async getGoalsByTeamId(
        teamId: string,
        userId?: string,
        filters?: {
            status?: string[];
            priority?: string[];
            leader?: string[];
            contributor?: string[];
            project?: string[];
        },
        groupBy?: string,
        orderBy = 'register_date',
        page = 0,
        pageSize = 20,
        includeAllInGroup = true
    ): Promise<GoalWithInfos[]> {
        const currentUserId =
            userId || (await authService.getCurrentUser())?.id;

        try {
            // 1. RPC Function으로 접근 가능한 goalIds 조회
            const { data: goalIdsData, error: idsError } = await supabase.rpc(
                'get_team_goal_ids',
                { p_team_id: teamId }
            );

            if (idsError) throw idsError;

            // 공유된 목표가 없는 경우 빈 배열 반환
            if (!goalIdsData || goalIdsData.length === 0) {
                return [];
            }

            // 2. 조회된 goal_id 목록을 이용하여 goal 테이블에서 상세 데이터 조회
            const goalIds = goalIdsData.map(
                (item: { goal_id: string }) => item.goal_id
            );

            // 기본 쿼리 구성
            let query = supabase
                .from('goal')
                .select(
                    `
                    *,
                    project:project!project_id (
                        id,
                        name,
                        register_date
                    ),
                    parent:goal!parent_id (*),
                    leader:user!leader_id (
                        id,
                        name,
                        profile_image_url
                    ),
                    todos:todo!goal_id (
                        *,
                        user:user_id (
                            id,
                            name,
                            profile_image_url
                        )
                    ),
                    participants:goal_participant!goal_id(
                        user!user_id (
                            id,
                            name,
                            profile_image_url
                        )
                    ),
                    visibility:user_goal_visibility!left(
                        is_hidden
                    )
                `
                )
                .in('id', goalIds)
                .is('enable', true)
                .filter('todos.enable', 'eq', true)
                .filter('visibility.user_id', 'eq', currentUserId);
            // .filter('shared_teams.enable', 'eq', true);

            // 서버측 필터 적용
            if (filters) {
                // 상태 필터
                if (filters.status && filters.status.length > 0) {
                    query = query.in('status_code', filters.status);
                }

                // 우선순위 필터
                if (filters.priority && filters.priority.length > 0) {
                    query = query.in('priority_code', filters.priority);
                }

                // 리더 필터
                if (filters.leader && filters.leader.length > 0) {
                    query = query.in('leader_id', filters.leader);
                }

                // 참여자 필터
                if (filters.contributor && filters.contributor.length > 0) {
                    query = query.in(
                        'participants.user_id',
                        filters.contributor
                    );
                }

                // 프로젝트 필터
                if (filters.project && filters.project.length > 0) {
                    const hasUndefinedProject =
                        filters.project.includes('undefined');

                    if (hasUndefinedProject && filters.project.length === 1) {
                        // '미분류' 프로젝트만 선택된 경우
                        query = query.is('project_id', null);
                    } else if (
                        hasUndefinedProject &&
                        filters.project.length > 1
                    ) {
                        // '미분류'와 다른 프로젝트가 함께 선택된 경우
                        const projectIds = filters.project.filter(
                            id => id !== 'undefined'
                        );
                        query = query.or(
                            `project_id.in.(${projectIds.join(',')}),project_id.is.null`
                        );
                    } else {
                        // 일반 프로젝트만 선택된 경우
                        query = query.in('project_id', filters.project);
                    }
                }
            }

            // 그룹핑이 있는 경우 그룹 내 모든 항목을 포함
            if (groupBy && includeAllInGroup) {
                // 그룹핑에 따른 처리
                if (groupBy === 'status') {
                    query = query.order('status_code', { ascending: true });
                } else if (groupBy === 'priority') {
                    query = query.order('priority_code', { ascending: false });
                } else if (groupBy === 'project') {
                    query = query.order('project_id', { ascending: true });
                }
            }

            // 정렬 적용
            if (orderBy === 'register_date') {
                query = query.order('register_date', { ascending: false });
            } else if (orderBy === 'update_date') {
                query = query.order('update_date', { ascending: false });
            } else if (orderBy === 'title') {
                query = query.order('title', { ascending: true });
            } else if (orderBy === 'due_date') {
                // null 값은 맨 마지막에 오도록 처리
                query = query.order('due_date', {
                    ascending: true,
                    nullsFirst: false,
                });
            } else if (orderBy === 'complete_date') {
                query = query.order('complete_date', {
                    ascending: false,
                    nullsFirst: false,
                });
            }

            // 페이지네이션 적용
            // 그룹핑이 있고 첫 페이지인 경우 충분한 데이터를 한 번에 가져옴
            if (groupBy && page === 0) {
                // 첫 페이지에서는 더 많은 데이터(30개가 기본 페이지 크기일 경우 90개)를 로드
                query = query.range(0, pageSize * 3 - 1);
            } else {
                // 이후 페이지는 정상적인 페이지 크기로 로드
                query = query.range(page * pageSize, (page + 1) * pageSize - 1);
            }

            const { data, error } = await query;

            if (error) throw error;

            const result =
                data?.map(goal => ({
                    ...goal,
                    participants: goal.participants?.map(p => p.user) ?? [],
                    is_hidden: goal.visibility?.[0]?.is_hidden ?? false,
                    // shared_teams: goal.shared_teams?.map(s => s.team) ?? [],
                })) || [];

            return result;
        } catch (error) {
            console.error(
                `Failed to get shared goals for teamId ${teamId}`,
                error
            );
            throw error;
        }
    },

    async getGoalDetailsById(
        goalId: string | undefined
    ): Promise<GoalWithDetails | null> {
        if (!goalId) return null;

        try {
            // 쿼리를 두 부분으로 나눕니다: 메인 목표 정보와 하위 목표 정보

            // 1. 메인 목표 쿼리 - 하위 목표(children) 제외
            interface MainGoalQueryResult extends Goal {
                team: TeamBase;
                // shared_teams?: { team: TeamBase }[];
                project?: ProjectBase & {
                    shared_teams?: { team: TeamBase }[];
                };
                parent?: Pick<Goal, 'id' | 'title'> | null;
                leader: Pick<User, 'id' | 'name' | 'profile_image_url'>;
                todos?: TodoWithPlanners[];
                participants?: {
                    user: Pick<User, 'id' | 'name' | 'profile_image_url'>;
                }[];
            }

            const { data: mainGoalData, error: mainGoalError } = await supabase
                .from('goal')
                .select(
                    `
                    *,
                    team:team_id (
                        id,
                        name,
                        profile_image_url
                    ),
                    project:project_id (
                        id,
                        name,
                        register_date,
                        shared_teams:project_sharing!project_id (
                            team!team_id (
                                id,
                                name,
                                profile_image_url
                            )
                        )
                    ),
                    parent:parent_id (
                        id,
                        title
                    ),  
                    leader:leader_id (
                        id,
                        name,
                        profile_image_url
                    ),
                    todos:todo (
                        *,
                        user:user_id (
                            id,
                            name,
                            profile_image_url
                        ),
                        planners:planner (
                            id,
                            planner_date,
                            blocks
                        )
                    ),
                    participants:goal_participant!goal_id(
                        user!user_id (
                            id,
                            name,
                            profile_image_url
                        )
                    )
                `
                )
                .eq('id', goalId)
                // .eq('enable', true)
                .filter('todos.enable', 'eq', true)
                // .filter('shared_teams.enable', 'eq', true)
                .filter('project.shared_teams.enable', 'eq', true)
                .single();

            if (mainGoalError) throw mainGoalError;
            if (!mainGoalData) return null;

            // 2. 하위 목표 쿼리 - 별도로 조회
            const { data: childrenData, error: childrenError } = await supabase
                .from('goal')
                .select(
                    `
                    *,
                    todos:todo(
                        *,
                        user:user_id(
                            id,
                            name,
                            profile_image_url
                        )
                    ),
                    leader:user!leader_id(
                        id,
                        name,
                        profile_image_url
                    )
                `
                )
                .eq('parent_id', goalId)
                .eq('enable', true)
                .filter('todos.enable', 'eq', true);
            // .filter('shared_teams.enable', 'eq', true);

            if (childrenError) throw childrenError;

            // 타입을 명시적으로 지정
            const typedMainData =
                mainGoalData as unknown as MainGoalQueryResult;
            const children = [
                ...childrenData.map(child => ({
                    ...child,
                    // shared_teams: child.shared_teams?.map(s => s.team) ?? [],
                })),
            ] as GoalWithTodos[];

            // 최종 응답 구성
            const response: GoalWithDetails = {
                ...typedMainData,
                project: {
                    ...typedMainData.project,
                    shared_teams:
                        typedMainData.project?.shared_teams?.map(s => s.team) ??
                        [],
                },
                todos:
                    typedMainData.todos?.sort(
                        (a: TodoWithPlanners, b: TodoWithPlanners) =>
                            new Date(b.register_date).getTime() -
                            new Date(a.register_date).getTime()
                    ) ?? [],
                participants:
                    typedMainData.participants?.map(p => p.user) ?? [],
                children:
                    children.sort(
                        (a: GoalWithTodos, b: GoalWithTodos) =>
                            new Date(b.register_date).getTime() -
                            new Date(a.register_date).getTime()
                    ) ?? [],
                // shared_teams:
                //     typedMainData.shared_teams?.map(s => s.team) ?? [],
            };

            return response;
        } catch (error) {
            console.error('Error fetching goal details:', error);
            throw error;
        }
    },

    async getGoalDetailsByProjectId(
        projectId: string
    ): Promise<GoalWithInfos[]> {
        const currentUserId = (await authService.getCurrentUser())?.id;
        try {
            const { data, error } = await supabase
                .from('goal')
                .select(
                    `
                    *,
                    project:project!project_id (
                        id,
                        name,
                        register_date
                    ),
                    parent:goal!parent_id (*),
                    leader:user!leader_id (
                        id,
                        name,
                        profile_image_url
                    ),
                    todos:todo!goal_id (
                        *,
                        user:user_id (
                            id,
                            name,
                            profile_image_url
                        )
                    ),
                    participants:goal_participant!goal_id(
                        user!user_id (
                            id,
                            name,
                            profile_image_url
                        )
                    ),
                    visibility:user_goal_visibility!left(
                        is_hidden
                    )
                `
                )
                .eq('project_id', projectId)
                .eq('enable', true)
                .filter('todos.enable', 'eq', true)
                .filter('visibility.user_id', 'eq', currentUserId)
                // .filter('shared_teams.enable', 'eq', true)
                .order('register_date', { ascending: false });

            if (error) throw error;

            const result =
                data?.map(goal => ({
                    ...goal,
                    participants:
                        // goal.leader
                        //     ? [
                        //           goal.leader,
                        //           ...(goal.participants?.map(p => p.user) ?? []),
                        //       ]
                        //     :
                        goal.participants?.map(p => p.user) ?? [],
                    // shared_teams: goal.shared_teams?.map(s => s.team) ?? [],
                })) || [];

            return result;
        } catch (error) {
            console.error(
                `Failed to get goal details by projectId ${projectId}`,
                error
            );
            throw error;
        }
    },

    async getGoalById(goalId: string): Promise<Goal> {
        try {
            const { data, error } = await supabase
                .from('goal')
                .select('*')
                .eq('id', goalId)
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error(`Failed to fetch goal ${goalId}`, error);
            throw error;
        }
    },

    async getUserGoals(
        userId?: string,
        visibility: GoalVisibilityFilter = GoalVisibilityFilter.VISIBLE
    ): Promise<Partial<GoalWithInfos>[]> {
        const currentUserId =
            userId || (await authService.getCurrentUser())?.id;

        try {
            const { data, error } = await supabase
                .from('goal')
                .select(
                    `
                    *,
                    project:project_id(
                        id,
                        name,
                        register_date
                    ),
                    leader:leader_id(
                        id,
                        name,
                        profile_image_url
                    ),
                    goal_participant!inner(*),
                    visibility:user_goal_visibility!left(
                        is_hidden
                    )
                `
                )
                .is('enable', true)
                .filter('visibility.user_id', 'eq', currentUserId)
                .eq('goal_participant.user_id', userId);

            if (error) throw error;

            // 해당 사용자가 워크스페이스 멤버인지 확인
            const accessCheckResults = await Promise.all(
                data?.map(async goal => {
                    if (!goal.team_id) {
                        return { goal, isAllowed: true }; // 팀이 없는 경우는 항상 허용
                    }

                    const { isAllowed } =
                        await workspaceService.checkUserWorkspaceMembership(
                            goal.team_id,
                            userId
                        );

                    return { goal, isAllowed };
                })
            );

            const result =
                accessCheckResults
                    ?.filter(result => result.isAllowed)
                    ?.map(goal => {
                        return {
                            ...goal.goal,
                            is_hidden:
                                goal.goal.visibility?.[0]?.is_hidden ?? false,
                        };
                    }) || [];

            return result;
        } catch (error) {
            console.error('Failed to fetch user goals', error);
            throw error;
        }
    },

    async getMemberGoalsByTeamIds(
        memberId: string,
        teamIds: string[]
    ): Promise<GoalWithProject[]> {
        try {
            // 1. 관리자인 목표
            const { data: managerGoals, error: managerError } = await supabase
                .from('goal')
                .select(
                    `
                      *,
                      project:project_id(
                        id,
                        name,
                        register_date
                      )
                    `
                )
                .eq('leader_id', memberId)
                .in('team_id', teamIds)
                .is('enable', true);

            if (managerError) throw managerError;

            // 2. 참여자인 목표
            // 2-1. goal id 조회
            const { data: participantGoalIds, error: participantError } =
                await supabase
                    .from('goal_participant')
                    .select('goal_id')
                    .eq('user_id', memberId);

            if (participantError) throw participantError;

            // 2-2. 관리자인 목표는 제거
            const managerGoalIds = managerGoals?.map(g => g.id) || [];
            const filteredParticipantGoalIds = (
                participantGoalIds?.map(p => p.goal_id) || []
            ).filter(id => !managerGoalIds.includes(id));

            // 2-3. goal 조회
            const { data: participantGoals, error: goalError } = await supabase
                .from('goal')
                .select(
                    `
                       *,
                       project:project_id(
                        id,
                        name,
                        register_date
                      )
                    `
                )
                .in('id', Array.from(filteredParticipantGoalIds))
                .in('team_id', teamIds)
                .is('enable', true);

            if (goalError) throw goalError;

            return [...managerGoals, ...participantGoals];
        } catch (error) {
            console.error('Failed to fetch member goals by team ids', error);
            throw error;
        }
    },

    async getGoalParticipantIdsByGoalId(goalId: string) {
        try {
            const { data, error } = await supabase
                .from('goal_participant')
                .select('user_id')
                .eq('goal_id', goalId);

            if (error) throw error;

            // 중복없는 배열 리턴
            return Array.from(new Set(data.map(item => item.user_id)));
        } catch (error) {
            console.error('Failed to fetch goal participants', error);
            throw error;
        }
    },

    async addGoalParticipant(
        goalId: string,
        userId: string,
        registerId: string
    ): Promise<boolean> {
        try {
            const { error } = await supabase.from('goal_participant').upsert(
                {
                    goal_id: goalId,
                    user_id: userId,
                    register_id: registerId,
                    register_date: new Date().toISOString(),
                    update_id: registerId,
                    update_date: new Date().toISOString(),
                },
                { onConflict: 'goal_id,user_id' }
            );

            if (error) throw error;
            return true;
        } catch (error) {
            console.error('Failed to add goal participant', error);
            return false;
        }
    },

    async removeGoalParticipant(
        goalId: string,
        userId: string
    ): Promise<boolean> {
        try {
            const { error } = await supabase
                .from('goal_participant')
                .delete()
                .match({
                    goal_id: goalId,
                    user_id: userId,
                });

            if (error) throw error;
            return true;
        } catch (error) {
            console.error('Failed to remove goal participant', error);
            return false;
        }
    },

    async createGoal(params: CreateGoalParams): Promise<Goal> {
        try {
            const { data, error } = await supabase
                .from('goal')
                .insert({
                    id: crypto.randomUUID(),
                    ...params,
                })
                .select()
                .single();

            if (error) throw error;

            // TODO: 팀간 목표 공유 관련 코드 점진적 제거
            if (params.parent_id) {
                const sharingTeamIds = await goalService.getGoalSharingTeamIds(
                    params.parent_id
                );
                if (sharingTeamIds?.length > 0) {
                    for (const teamId of sharingTeamIds) {
                        await goalService.updateGoalSharing(
                            data.id,
                            teamId,
                            true
                        );
                    }
                }
            }

            return data;
        } catch (error) {
            console.error('Failed to create goal', error);
            throw error;
        }
    },

    async updateGoal(id: string, fields: Partial<Goal>): Promise<Goal> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { data, error } = await supabase
                .from('goal')
                .update({
                    ...fields,
                    update_id: user.id,
                    update_date: new Date().toISOString(),
                })
                .eq('id', id)
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error(`Failed to update goal ${id}`, error);
            throw error;
        }
    },

    async hideGoal(goalId: string, userId: string): Promise<void> {
        try {
            // 1. user_goal_visibility 테이블에 레코드 생성/존재하면 업데이트
            const { data, error } = await supabase
                .from('user_goal_visibility')
                .upsert(
                    {
                        goal_id: goalId,
                        user_id: userId,
                        is_hidden: true,
                        register_date: new Date().toISOString(),
                        update_date: new Date().toISOString(),
                    },
                    // 충돌 판단 컬럼 - 이것 기준으로 이미 존재하는 레코드인지 판단
                    {
                        onConflict: 'goal_id,user_id',
                    }
                )
                .select();

            if (error) throw error;

            // 2. 해당 Goal 하위의 모든 Todo 숨기기
            await supabase
                .from('todo')
                .update({
                    is_hidden: true,
                    update_date: new Date().toISOString(),
                    update_id: userId,
                })
                .eq('goal_id', goalId)
                .eq('user_id', userId);
        } catch (error) {
            console.error('Failed to hide goal', error);
            throw error;
        }
    },

    // goal 다시 표시하기
    async revealGoal(goalId: string, userId: string): Promise<void> {
        try {
            // 1. user_goal_visibility 테이블에서 is_hidden false로 업데이트
            const { data, error } = await supabase
                .from('user_goal_visibility')
                .upsert(
                    {
                        goal_id: goalId,
                        user_id: userId,
                        is_hidden: false,
                        register_date: new Date().toISOString(),
                        update_date: new Date().toISOString(),
                    },
                    { onConflict: 'goal_id,user_id' }
                );

            if (error) throw error;
        } catch (error) {
            console.error('Failed to reveal goal', error);
            throw error;
        }
    },

    async deleteGoal(goalId: string): Promise<void> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { error } = await supabase
                .from('goal')
                .update({
                    enable: false,
                    update_id: user.id,
                    update_date: new Date().toISOString(),
                })
                .eq('id', goalId);

            if (error) throw error;
        } catch (error) {
            console.error(`Failed to delete goal ${goalId}`, error);
            throw error;
        }
    },

    // TODO: 팀간 목표 공유 관련 코드 점진적 제거
    async getGoalSharingTeamIds(goalId: string): Promise<string[]> {
        try {
            const { data, error } = await supabase
                .from('goal_sharing')
                .select('team_id')
                .eq('goal_id', goalId)
                .eq('enable', true);

            if (error) throw error;
            return data?.map(item => item.team_id) || [];
        } catch (error) {
            console.error(
                `Failed to get sharing teams for goal ${goalId}`,
                error
            );
            throw error;
        }
    },

    // TODO: 팀간 목표 공유 관련 코드 점진적 제거
    async updateGoalSharing(
        goalId: string,
        teamId: string,
        enable: boolean,
        includeChildGoals = false
    ): Promise<void> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            // 하위 목표도 같이 공유 여부
            if (includeChildGoals) {
                // 하위 목표도 같이 공유 여부
                const { data: childGoals } = await supabase
                    .from('goal')
                    .select('id')
                    .eq('parent_id', goalId)
                    .eq('enable', true);

                if (childGoals?.length > 0) {
                    for (const childGoal of childGoals) {
                        await goalService.updateGoalSharing(
                            childGoal.id,
                            teamId,
                            enable
                        );
                    }
                }
            }

            // 이미 공유 기록이 있는지 확인
            const { data: existingShare } = await supabase
                .from('goal_sharing')
                .select('*')
                .eq('goal_id', goalId)
                .eq('team_id', teamId)
                .maybeSingle();

            // 현재 요청된 상태와 기존 상태 비교
            if (existingShare) {
                // 상태가 같으면 아무 작업도 하지 않음
                if (existingShare.enable === enable) return;

                // 상태가 다르면 업데이트
                const { error } = await supabase
                    .from('goal_sharing')
                    .update({
                        enable: enable,
                        update_date: new Date().toISOString(),
                        update_id: user.id,
                    })
                    .eq('id', existingShare.id);

                if (error) throw error;
            } // 공유 기록이 없고 공유 요청인 경우에만 새로 생성
            else if (enable) {
                const { error } = await supabase.from('goal_sharing').insert({
                    goal_id: goalId,
                    team_id: teamId,
                    register_id: user.id,
                    enable: true,
                    is_owner: false,
                });

                if (error) throw error;
            }
        } catch (error) {
            console.error(
                `Failed to update goal sharing for goal ${goalId} with team ${teamId}`,
                error
            );
            throw error;
        }
    },

    // TODO: 팀간 목표 공유 관련 코드 점진적 제거
    /**
     * 하위 목표들의 공유 상태를 확인하는 함수
     * @param goalId 상위 목표 ID
     * @param teamId 팀 ID
     * @returns 하위 목표 공유 상태 정보 (전체 공유 여부, 공유된 목표 수, 총 하위 목표 수)
     */
    async checkChildrenSharingStatus(
        goalId: string,
        teamId: string
    ): Promise<{
        allShared: boolean;
        sharedCount: number;
        childrenCount: number;
    }> {
        try {
            if (!goalId || !teamId) {
                return { allShared: true, sharedCount: 0, childrenCount: 0 };
            }

            // 하위 목표들 가져오기
            const { data: children, error: childrenError } = await supabase
                .from('goal')
                .select('id')
                .eq('parent_id', goalId)
                .eq('enable', true);

            if (childrenError) throw childrenError;
            if (!children || children.length === 0) {
                return { allShared: true, sharedCount: 0, childrenCount: 0 };
            }

            const childrenIds = children.map(c => c.id);

            // 공유된 하위 목표들 가져오기
            const { data: sharedChildren, error: sharingError } = await supabase
                .from('goal_sharing')
                .select('goal_id')
                .in('goal_id', childrenIds)
                .eq('team_id', teamId)
                .eq('enable', true);

            if (sharingError) throw sharingError;

            const sharedCount = sharedChildren?.length || 0;
            const childrenCount = children.length;

            return {
                allShared: sharedCount === childrenCount,
                sharedCount,
                childrenCount,
            };
        } catch (error) {
            console.error(
                `Failed to check children sharing status for goal ${goalId} with team ${teamId}`,
                error
            );
            return { allShared: false, sharedCount: 0, childrenCount: 0 };
        }
    },

    async checkGoalAccess(goalId: string): Promise<boolean> {
        const user = await authService.getCurrentUser();
        if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

        try {
            // 워크스페이스 멤버십을 통한 직접 접근 확인
            const { data: directAccess, error } = await supabase
                .from('user_goal_access')
                .select('goal_id')
                .eq('goal_id', goalId)
                .eq('user_id', user?.id)
                .maybeSingle();

            if (directAccess) {
                return true;
            }

            return false;
        } catch (error) {
            console.error('Error checking goal access:', error);
            return false;
        }
    },
};
